import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { admin } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    admin
      .me()
      .then(() => nav("/admin/dashboard"))
      .catch(() => setChecking(false));
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await admin.login(username, password);
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="mx-auto w-10 h-10 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 grain relative overflow-hidden">
      <div
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(234,179,8,0.15), transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      <div className="relative z-10 w-full max-w-sm">
        <div className="mb-8 text-center">
          <div
            className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{
              background: "linear-gradient(135deg, #eab308, #f59e0b)",
              boxShadow: "0 0 24px rgba(234,179,8,0.5)",
            }}
          >
            <ShieldCheck className="w-6 h-6 text-black" />
          </div>
          <h1 className="font-heading font-black text-2xl tracking-tighter">Admin Access</h1>
          <div className="text-xs text-zinc-500 mt-1 font-mono tracking-widest">
            HOUSEPARTY · CONTROL ROOM
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-zinc-800 bg-zinc-950/80 backdrop-blur p-6 space-y-4"
        >
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block font-mono tracking-widest">
              USERNAME
            </Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              required
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="admin-username"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block font-mono tracking-widest">
              PASSWORD
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="admin-password"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-mono font-bold tracking-widest text-xs py-6"
            data-testid="admin-login-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </Button>
        </form>

        <div className="mt-6 text-center text-[10px] text-zinc-600 font-mono tracking-widest">
          RESTRICTED · STAFF ONLY
        </div>
      </div>
    </div>
  );
}
